/* The lead record: the same shape as the member record, on purpose.
 *
 * A prospect and a member are ONE PERSON at two points of one journey (SITE-RULES
 * 11c), so the record reads the same way before and after they join: overview, then
 * the one activity timeline, then the slices. The two intake assessments are ALWAYS
 * present rather than hidden when empty - an unanswered assessment is the normal
 * state of a new prospect and is exactly the work the front desk is being asked to
 * go and do, so hiding the empty form hides the job.
 */
import { useState } from 'react';
import { useAsync, useSource } from '../data/SourceProvider.js';
import { NoneRow, Panel } from '../components/Panel.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { FieldList } from './record/Tabs.js';
import { Timeline } from './record/Timeline.js';
import { LeadHeader } from './lead/Header.js';
import { LeadOverview } from './lead/Overview.js';
import { BookTrialModal, PRIMARY, StageModal } from './lead/Actions.js';
import { STAGES } from '../lib/leads.js';
import { fmt } from '../lib/fmt.js';
import { go } from '../routes.js';

const TABS = [
  ['overview', 'Overview'],
  ['timeline', 'Activity Timeline'],
  ['marketing', 'Marketing'],
  ['afp', 'Fitness Plan'],
  ['details', 'Details'],
  ['notes', 'Notes'],
] as const;

type TabKey = (typeof TABS)[number][0];

export function LeadRecord({ id }: { id: string }) {
  const source = useSource();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>('overview');
  const [noting, setNoting] = useState(false);
  const [booking, setBooking] = useState(false);
  const [staging, setStaging] = useState<null | { to: string | null }>(null);
  const [epoch, setEpoch] = useState(0);
  const { data: l, error } = useAsync(() => source.getLead(id), [source, id, epoch]);

  if (error) return <NoneRow>Could not read the record. {error.message}</NoneRow>;
  if (!l) return <NoneRow>Reading the record…</NoneRow>;

  const won = l.stage === 'won';
  const primary = PRIMARY[l.stage];

  return (
    <>
      <button type="button" className="linkish t-label-small" onClick={() => go('/leads')}>
        ← Pipeline
      </button>

      {/* One person, one journey. A won lead's attendance, scans and billing live on
          their member record, and the way there is the loudest thing on the page. */}
      {won && l.memberId && (
        <section className="panel converted">
          <div className="t-label-large">Converted to member</div>
          <p className="t-body-small" style={{ marginTop: 4 }}>
            This lead became a member record: attendance, InBody scans and billing live there.
          </p>
          <button type="button" className="btn t-label-small" style={{ marginTop: 8 }}
            onClick={() => go('/members/' + l.memberId)}>Open member record →</button>
        </section>
      )}

      <LeadHeader
        l={l}
        actions={
          <>
            {/* The stage's own next move leads, and it is the same move the cadence
                strip beside it names. A lead with both trials done must not offer
                the same first button as one nobody has called. */}
            {primary && (
              <button
                type="button" className="btn primary t-label-medium"
                onClick={() => {
                  if (primary.kind === 'trial') setBooking(true);
                  else if (primary.kind === 'stage') setStaging({ to: primary.to ?? null });
                  else setNoting(true);
                }}
              >{primary.label}</button>
            )}
            <button type="button" className={'btn t-label-medium' + (primary ? '' : ' primary')}
              onClick={() => setNoting(true)}>Add note</button>
            {!won && (
              <button type="button" className="btn t-label-medium" onClick={() => setStaging({ to: null })}>
                Move stage
              </button>
            )}
            {!won && primary?.kind !== 'trial' && l.stage !== 'lost' && (
              <button type="button" className="btn t-label-medium" onClick={() => setBooking(true)}>
                Book trial
              </button>
            )}
            {l.memberId && (
              <button type="button" className="btn t-label-medium" onClick={() => go('/members/' + l.memberId)}>
                View member record →
              </button>
            )}
          </>
        }
      />

      <div className="tabstrip" role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key} type="button" role="tab" aria-selected={tab === key}
            className={'tab t-label-medium' + (tab === key ? ' on' : '')}
            onClick={() => setTab(key)}
          >{label}</button>
        ))}
      </div>

      {tab === 'overview' && <LeadOverview l={l} onNote={() => setNoting(true)} />}
      {tab === 'timeline' && <Timeline id={l.id} />}

      {tab === 'marketing' && (
        <Panel eyebrow="How they found you" title="Marketing">
          <FieldList answered fields={[
            ['Source', l.source], ['Source detail', l.sourceDetail],
            ['Heard about us', l.heard], ['Referred by', null],
            ['Email opt in', null], ['SMS opt in', null],
            ['Last campaign', null], ['Replied', null],
          ]} />
        </Panel>
      )}

      {tab === 'afp' && (
        <Panel eyebrow="Intake" title="Fitness plan">
          <FieldList answered fields={[
            ['Primary goal', l.goal], ['Why now', l.why],
            ['Target weight', null], ['Target body fat', null],
            ['100 day goal', null], ['12 month goal', null],
            ['Training history', null], ['Injuries', null],
            ['Days available', null], ['Preferred time', null],
          ]} />
        </Panel>
      )}

      {tab === 'details' && (
        <Panel eyebrow="The system record" title="Details">
          <FieldList fields={[
            ['First name', l.firstName], ['Last name', l.lastName],
            ['Email', l.email], ['Phone', l.phone],
            ['Stage', STAGES[l.stage].long],
            ['Created', fmt.date(l.createdAt)],
            ['Last activity', l.lastActivity ? fmt.date(l.lastActivity) : null],
            ['Routed to', l.owner],
            ['Anchor', l.anchor?.name ?? null],
            ['Anchor basis', l.anchor?.basis ?? null],
            ['Est. value', l.valueCents == null ? null : fmt.money(l.valueCents) + '/mo'],
            ['Next action', l.nextAction],
            ['Dropout reason', l.lostReason],
            ['In their words', l.lostReasonDetail],
            ['Member record', l.memberId],
          ]} />
        </Panel>
      )}

      {tab === 'notes' && (
        <Panel
          eyebrow="What staff wrote down" title="Notes"
          right={<button type="button" className="btn t-label-small" onClick={() => setNoting(true)}>Add note</button>}
        >
          {!l.notes.length ? <NoneRow>No activity logged for this lead.</NoneRow> : (
            <div className="rows">
              {[...l.notes].sort((a, b) => b.at.localeCompare(a.at)).map((n, i) => (
                <div className="row" key={`${n.at}-${i}`}>
                  <div className="who">
                    <div className="n t-body-medium">{n.text}</div>
                    <div className="s t-caption muted">{[n.who, fmt.date(n.at)].filter(Boolean).join(' · ')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {booking && (
        <BookTrialModal l={l} onClose={() => setBooking(false)}
          onDone={() => { setBooking(false); setEpoch((n) => n + 1); }} />
      )}

      {staging && (
        <StageModal l={l} to={staging.to as never} onClose={() => setStaging(null)}
          onDone={() => { setStaging(null); setEpoch((n) => n + 1); }} />
      )}

      {noting && (
        <NoteModal
          name={l.name}
          onClose={() => setNoting(false)}
          onSave={async (text) => {
            await source.addLeadNote(l.id, text);
            setNoting(false);
            setEpoch((n) => n + 1);
            toast('Note added', 'good');
          }}
        />
      )}
    </>
  );
}

/* A note is EVIDENCE the cadence reads, not just a comment: addLeadNote writes it to
   the one event stream as well as to the record, which is what makes "Day 2: book the
   first trial" go green when somebody actually did it. */
function NoteModal({ name, onClose, onSave }: {
  name: string; onClose: () => void; onSave: (text: string) => Promise<void>;
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <Modal
      title={'Add note · ' + name} onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button
            type="button" className="btn primary t-label-medium"
            disabled={saving || !text.trim()}
            onClick={() => { setSaving(true); void onSave(text.trim()).catch(() => setSaving(false)); }}
          >{saving ? 'Saving…' : 'Save note'}</button>
        </>
      }
    >
      <textarea
        className="field" autoFocus rows={4} value={text}
        placeholder="What was said, and what happens next"
        onChange={(e) => setText(e.target.value)}
      />
    </Modal>
  );
}
